"use client";
import { useState, useEffect, useRef } from 'react'
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import Image from 'next/image';

import { Menu, ClipboardList, Download, Search, X, Globe } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { useQuoteStore } from '@/stores/quoteStore'
import { useSearchStore } from '@/stores/searchStore'
import { useCataloguePDF } from '@/lib/catalogue/useCataloguePDF'
import { siteConfig } from '@/data/siteConfig'
import { cn } from '@/lib/utils'

export function Header() {
  const [searchOpen, setSearchOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)
  const router = useRouter()
  const pathname = usePathname()
  const quoteCount = useQuoteStore((s) => s.items.length)
  const { query, results, isLoading, search, clearSearch } = useSearchStore()
  const { generateFullCatalogue, isGenerating } = useCataloguePDF()

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const handleSearch = (value: string) => {
    if (value.trim()) {
      search(value)
      setSearchOpen(true)
    } else {
      clearSearch()
      setSearchOpen(false)
    }
  }

  const handleResultClick = (slug: string) => {
    setSearchOpen(false)
    clearSearch()
    router.push(`/product/${slug}`)
  }

  const navLinks = [
    { label: 'Tower Bolts', href: '/tower-bolts' },
    { label: 'Cabinet Handles', href: '/cabinet-handles', extraSlugs: ['cabinate-handle', 'door-handle'] },
    { label: 'Hinges', href: '/hinges' },
    { label: 'About', href: '/about' },
    { label: 'Export', href: '/export' },
    { label: 'Inquiry', href: '/quick-inquiry' },
  ]

  if (pathname.startsWith('/admin')) {
    return null;
  }

  return (
    <>
      {/* Export ticker */}
      <div className="bg-primary text-primary-foreground text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-primary-foreground/70">
            <Globe className="h-3 w-3" />
            <span className="hidden sm:inline">Exporting to 30+ countries · FOB / CIF / DDP</span>
            <span className="sm:hidden">Worldwide Export</span>
          </div>
          <div className="flex items-center gap-3 text-primary-foreground/60">
            <span className="hidden md:inline">USD · EUR · GBP · AED · INR</span>
            <span className="font-medium text-brand-accent">ISO 9001:2015</span>
          </div>
        </div>
      </div>

      <header
        className={cn(
          'sticky top-0 z-50 transition-all duration-300 border-b',
          scrolled
            ? 'bg-background/85 backdrop-blur-xl border-border/60 shadow-sm'
            : 'bg-background border-transparent'
        )}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link href="/" className="flex-shrink-0 flex items-center gap-2.5" aria-label="Jamnagar Metals home">
              <Image
                src="/logo-v2.svg"
                alt="Jamnagar Metals"
                width={180}
                height={40}
                className="h-10 w-auto hidden sm:block"
                priority
              />
              <Image
                src="/vertival-logo-v2.svg"
                alt="Jamnagar Metals"
                width={40}
                height={40}
                className="h-10 w-auto sm:hidden"
                priority
              />
            </Link>

            <nav className="hidden xl:flex items-center gap-1">
              {navLinks.map((link) => {
                const linkSlug = link.href.replace('/', '')
                const isProductPage = pathname.startsWith('/product/')
                const isActive = pathname === link.href || 
                               (isProductPage && (
                                 pathname.includes(linkSlug) || 
                                 link.extraSlugs?.some(slug => pathname.includes(slug))
                               ))
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    className={cn(
                      "px-3 py-2 text-sm font-medium transition-colors relative group",
                      isActive ? "text-foreground" : "text-foreground/70 hover:text-foreground"
                    )}
                  >
                    {link.label}
                    <span 
                      className={cn(
                        "absolute bottom-1 left-3 right-3 h-px bg-brand-accent transition-transform origin-left",
                        isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                      )} 
                    />
                  </Link>
                )
              })}
            </nav>

            <div className="flex items-center gap-1.5">
              <div ref={searchRef} className="relative hidden sm:block">
                <div className="flex items-center gap-1 bg-muted rounded-full px-3 h-9 border border-transparent focus-within:border-ring transition-colors">
                  <Search className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  <Input
                    placeholder="Search SKUs, products..."
                    className="border-0 bg-transparent focus-visible:ring-0 w-44 h-7 text-sm px-1 shadow-none"
                    value={query}
                    onChange={(e) => handleSearch(e.target.value)}
                    onFocus={() => results.length > 0 && setSearchOpen(true)}
                  />
                  {query && (
                    <button onClick={() => { clearSearch(); setSearchOpen(false) }}>
                      <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                    </button>
                  )}
                </div>
                {searchOpen && results.length > 0 && (
                  <div className="absolute top-full mt-2 right-0 w-80 bg-popover rounded-lg shadow-xl border border-border z-50 overflow-hidden">
                    {isLoading ? (
                      <div className="p-3 text-sm text-muted-foreground">Searching...</div>
                    ) : (
                      results.map((product) => (
                        <button
                          key={product.id}
                          className="w-full text-left px-3 py-2.5 text-sm hover:bg-muted flex flex-col gap-0.5 border-b border-border last:border-0"
                          onClick={() => handleResultClick(product.slug)}
                        >
                          <span className="font-medium text-foreground">{product.name}</span>
                          <span className="text-xs text-muted-foreground line-clamp-1">
                            {product.description}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              <Button
                size="sm"
                variant="ghost"
                onClick={generateFullCatalogue}
                disabled={isGenerating}
                className="hidden md:flex items-center gap-1.5 text-foreground/80 hover:text-foreground"
              >
                <Download className="h-3.5 w-3.5" />
                {isGenerating ? 'Preparing...' : 'Catalogue'}
              </Button>

              <Link href="/quote" className="relative">
                <Button size="sm" variant="ghost" className="relative" aria-label={`View quote basket (${quoteCount} items)`}>
                  <ClipboardList className="h-4 w-4" />
                  {quoteCount > 0 && (
                    <Badge className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 flex items-center justify-center text-[10px] border-0 bg-brand-accent text-brand-accent-foreground">
                      {quoteCount}
                    </Badge>
                  )}
                </Button>
              </Link>

              <Link href="/quote" className="hidden sm:block">
                <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium shadow-sm">
                  Request Quote
                </Button>
              </Link>

              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button size="sm" variant="ghost" className="xl:hidden" aria-label="Open menu">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-80 pt-12">
                  <nav className="flex flex-col gap-1">
                    {navLinks.map((link) => {
                      const linkSlug = link.href.replace('/', '')
                      const isProductPage = pathname.startsWith('/product/')
                      const isActive = pathname === link.href || 
                                     (isProductPage && (
                                       pathname.includes(linkSlug) || 
                                       link.extraSlugs?.some(slug => pathname.includes(slug))
                                     ))
                      return (
                        <Link
                          key={link.href}
                          href={link.href}
                          className={cn(
                            "px-3 py-2.5 text-sm font-medium rounded-md transition-colors",
                            isActive ? "bg-muted text-foreground" : "hover:bg-muted text-foreground/70 hover:text-foreground"
                          )}
                          onClick={() => setMobileOpen(false)}
                        >
                          {link.label}
                        </Link>
                      )
                    })}
                    <Separator className="my-2" />
                    <Link
                      href="/quote"
                      className="px-3 py-2.5 text-sm font-medium rounded-md hover:bg-muted flex items-center gap-2"
                      onClick={() => setMobileOpen(false)}
                    >
                      <ClipboardList className="h-4 w-4" />
                      Quote Basket
                      {quoteCount > 0 && (
                        <Badge className="ml-auto bg-brand-accent text-brand-accent-foreground text-xs">
                          {quoteCount}
                        </Badge>
                      )}
                    </Link>
                    <button
                      className="px-3 py-2.5 text-sm font-medium rounded-md hover:bg-muted flex items-center gap-2 text-left"
                      onClick={() => { generateFullCatalogue(); setMobileOpen(false) }}
                      disabled={isGenerating}
                    >
                      <Download className="h-4 w-4" />
                      {isGenerating ? 'Preparing Catalogue...' : 'Download Catalogue'}
                    </button>
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>
    </>
  )
}
